import {
  eventFeed,
  EventFeedRequest,
  EventFeedRequestV2,
  EventFeedResponse,
} from '@api';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';
import { optimisticQueriesUpdate } from '@terminal/utils/optimistic-updates/optimistic-updates';
import { useEffect } from 'react';

import {
  enrichEventsWithMarketOverviewInsights,
  fetchMarketOverviewInsights,
} from './utils';

export const QUERY_KEY_EVENT_FEED = 'QUERY_KEY_EVENT_FEED';

interface PageParam {
  page: number;
  next?: string;
  prev?: string;
}

const initialPageParam: PageParam = { page: 1 };

export interface UseEventFeedOptions {
  enabled?: boolean;
}

export const useEventFeed = (
  body: EventFeedRequestV2,
  options?: UseEventFeedOptions
) => {
  // TODO: remove this once the endpoint is live
  // const isFollowedThemesFlagEnabled = useFlags(state =>
  //   state.getFeatureFlagState('followed_themes')
  // );
  const isFollowedThemesFlagEnabled = false;
  const shouldFetchMarketOverview =
    body.type === undefined || body.type === 'market-overview';

  return useInfiniteQuery({
    queryKey: [QUERY_KEY_EVENT_FEED, body, isFollowedThemesFlagEnabled],
    enabled: options?.enabled ?? true,
    queryFn: async ({ signal, pageParam }) => {
      const path = isFollowedThemesFlagEnabled
        ? eventFeed.postFeedEventsV2.path
        : eventFeed.postFeedEvents.path;
      const schema = isFollowedThemesFlagEnabled
        ? eventFeed.postFeedEventsV2.schema
        : eventFeed.postFeedEvents.schema;
      const eventFeedResponse = await wretchRequest<EventFeedResponse>(path, {
        method: 'post',
        body: { ...body, ...pageParam },
        schema,
        signal,
      });

      const hasMarketOverviewEvents = eventFeedResponse.events.some(
        event => event.type === 'market-overview'
      );

      if (!shouldFetchMarketOverview || !hasMarketOverviewEvents) {
        return eventFeedResponse;
      }

      const marketOverviewData = await fetchMarketOverviewInsights(
        pageParam.page,
        signal
      );

      const enrichedEvents = enrichEventsWithMarketOverviewInsights(
        eventFeedResponse.events,
        marketOverviewData
      );

      return {
        ...eventFeedResponse,
        events: enrichedEvents,
      };
    },
    staleTime: 0,
    gcTime: 0,
    initialPageParam,
    getNextPageParam: (
      lastPage,
      _allPages,
      lastPageParam
    ): PageParam | undefined =>
      lastPage?.next_cursor
        ? { next: lastPage.next_cursor, page: lastPageParam.page + 1 }
        : undefined,
    getPreviousPageParam: (
      firstPage,
      _allPages,
      firstPageParam
    ): PageParam | undefined =>
      firstPage?.prev_cursor
        ? { prev: firstPage.prev_cursor, page: firstPageParam.page - 1 }
        : undefined,
  });
};

export const useEventFeedAllPages = (
  body: EventFeedRequest,
  options?: UseEventFeedOptions
) => {
  const result = useEventFeed(body, options);

  const lastPage = result.data?.pages?.at(-1);
  const lastEvent = lastPage?.events?.at(-1);
  const reachedDateBoundary =
    !!body.date_from &&
    !!lastEvent &&
    new Date(lastEvent.created_at) < new Date(body.date_from);

  useEffect(() => {
    if (
      result.hasNextPage &&
      !result.isFetchingNextPage &&
      !reachedDateBoundary
    ) {
      result.fetchNextPage();
    }
  }, [
    result.hasNextPage,
    result.isFetchingNextPage,
    result.fetchNextPage,
    reachedDateBoundary,
  ]);

  return result;
};

/**
 * @deprecated
 * all events return read false
 * https://toggleai.atlassian.net/browse/PLATFORM-3628
 */
export const postEventRead = (eventId: string) => {
  const { path } = eventFeed.postEventRead;
  return wretchRequest(path(eventId), {
    method: 'post',
  });
};

/**
 * @deprecated
 * all events return read false
 * https://toggleai.atlassian.net/browse/PLATFORM-3628
 * Hook for marking events as read with optimistic updates
 * Immediately updates the UI to show the event as read, then sends the API request
 */
export const useMarkEventRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => postEventRead(eventId),
    onMutate: async (eventId: string) => {
      const { previousData: previousQueries } = await optimisticQueriesUpdate<{
        pages: EventFeedResponse[];
        pageParams: PageParam[];
      }>({
        queryClient,
        queryKey: [QUERY_KEY_EVENT_FEED],
        updateFn: oldData => ({
          ...oldData,
          pages: oldData.pages.map(page => ({
            ...page,
            events: page.events.map(event =>
              event.id === eventId ? { ...event, read: true } : event
            ),
          })),
        }),
      });

      return { previousQueries };
    },
    onError: (_err, _eventId, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
};
